import { Link } from "react-router-dom";
import PageMeta from "@/components/feature/PageMeta";
import SectionHeader from "@/components/feature/SectionHeader";
import MediaGrid from "@/components/feature/MediaGrid";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import { mediaList, sortByNewest, sortByPopular, formatViews } from "@/lib/media";
import { newsList, sortNewsByTime } from "@/lib/news";

const LATEST = sortByNewest(mediaList).slice(0, 8);
const POPULAR = sortByPopular(mediaList).slice(0, 8);
const NEWS = sortNewsByTime(newsList).slice(0, 6);
const FEATURED_NEWS = NEWS[0];
const NEWS_REST = NEWS.slice(1);

const STATS = [
  { value: "1200+", label: "影视作品收录" },
  { value: "48万", label: "月活跃用户" },
  { value: "每日更新", label: "资讯与片单" },
];

export default function Home() {
  return (
    <>
      <PageMeta
        title="蜜桃成熟影视大全-电影电视剧剧情资讯聚合平台"
        description="蜜桃成熟为您提供丰富的电影、电视剧资源及剧情资讯，涵盖最新影视动态和热门片单，方便用户了解和选择喜爱的影视作品。"
      />

      <HeroCarousel />

      <div className="w-full px-4 md:px-8 py-10 md:py-14 space-y-14 md:space-y-20">
        <CategoryQuickLinks />

        <section>
          <SectionHeader
            title="最新上线"
            subtitle="按上线时间倒序，第一时间掌握新片与剧集"
            moreTo="/category/movie"
          />
          <MediaGrid items={LATEST} />
        </section>

        <section className="rounded-xl bg-background-100 border border-background-200 px-4 md:px-8 py-10 md:py-12">
          <SectionHeader
            title="热门推荐"
            subtitle="依据评分与浏览热度精选，口碑与话题兼具"
            moreTo="/category/tv"
            accent
          />
          <MediaGrid items={POPULAR} />
        </section>

        <section>
          <SectionHeader
            title="影视资讯精选"
            subtitle="行业动态、剧集花絮与影讯速递"
            moreTo="/news"
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {FEATURED_NEWS && (
              <Link
                to={`/news/detail/${FEATURED_NEWS.id}`}
                className="group flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
              >
                <div className="relative w-full h-52 md:h-64 overflow-hidden bg-background-100">
                  <img
                    src={FEATURED_NEWS.image}
                    alt={FEATURED_NEWS.title}
                    title={`${FEATURED_NEWS.title} 资讯详情`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs">
                    {FEATURED_NEWS.category}
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="text-base md:text-lg font-medium text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                    {FEATURED_NEWS.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3 line-clamp-2">
                    {FEATURED_NEWS.summary}
                  </p>
                  <p className="text-xs text-foreground-400 mt-4 flex items-center gap-3">
                    <span>{FEATURED_NEWS.publishTime}</span>
                    <span>{FEATURED_NEWS.author}</span>
                  </p>
                </div>
              </Link>
            )}

            <div className="flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              {NEWS_REST.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/detail/${item.id}`}
                  className="group flex gap-4 p-4 md:p-5 hover:bg-background-100 transition-colors"
                >
                  <div className="w-24 h-20 md:w-32 md:h-24 shrink-0 rounded-md overflow-hidden bg-background-100">
                    <img
                      src={item.image}
                      alt={item.title}
                      title={`${item.title} 资讯`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs text-primary-600">{item.category}</span>
                    <h3 className="text-sm md:text-base text-foreground-900 font-medium leading-snug line-clamp-2 mt-1 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{item.publishTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center rounded-xl border border-background-200 bg-primary-50 px-6 md:px-10 py-10 md:py-12">
          <div>
            <span className="text-xs font-label tracking-[0.3em] text-primary-700">ABOUT PEACH CINEMA</span>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mt-3 mb-4">
              关于蜜桃成熟
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              蜜桃成熟是一个专注电影与电视剧的内容聚合平台。我们把散落各处的影视资料整理成清晰的片单、剧情介绍与资讯速递，
              让你少一点查找的麻烦，多一点发现好作品的惊喜。这里不提供在线播放与下载，只专注于帮你「看见」更多值得期待的影视作品。
            </p>
            <div className="flex items-center gap-3 mt-6">
              <Link
                to="/about"
                className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm whitespace-nowrap transition-colors"
              >
                了解我们
              </Link>
              <Link
                to="/news"
                className="px-5 py-2.5 rounded-md border border-primary-300 text-primary-700 hover:bg-primary-100 text-sm whitespace-nowrap transition-colors"
              >
                浏览资讯
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {STATS.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-primary-200 bg-background-50 px-4 py-6 text-center"
              >
                <p className="text-xl md:text-2xl font-heading font-semibold text-primary-600">
                  {stat.value}
                </p>
                <p className="text-xs text-foreground-600 mt-2">{stat.label}</p>
              </div>
            ))}
            <div className="sm:col-span-3 flex items-center justify-between rounded-lg bg-primary-500 text-background-50 px-5 py-4">
              <div>
                <p className="text-sm font-medium">今日热度最高</p>
                <p className="text-xs text-background-100/90 mt-1 line-clamp-1">
                  {POPULAR[0]?.title} · {formatViews(POPULAR[0]?.views ?? 0)} 浏览
                </p>
              </div>
              <Link
                to={`/detail/${POPULAR[0]?.id}`}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/30 text-background-50 transition-colors shrink-0"
                aria-label="查看热门作品"
              >
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}